import moment from 'moment';
import 'moment-timezone';

/*
 * Roles, tenant scope and the active time zone for the logged-in operator.
 *
 * Roles (set by the API at login, mirrored from panel-api utils/roles.js):
 *   super_admin  — manages tenants; can act for one tenant or for all of them
 *   tenant_admin — manages one tenant's data and its panel users
 *   staff        — works inside one tenant
 *
 * A super admin's chosen tenant ("scope") lives in localStorage and is sent as
 * the X-Tenant-Id header by constants/authSession.js. The API ignores that
 * header for everyone else.
 */

export const ROLES = Object.freeze({
  SUPER_ADMIN: 'super_admin',
  TENANT_ADMIN: 'tenant_admin',
  STAFF: 'staff',
});

export const ROLE_LABELS = {
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.TENANT_ADMIN]: 'Tenant Admin',
  [ROLES.STAFF]: 'Staff',
};

export const DEFAULT_TENANT_ID = 1;
export const TENANT_SCOPE_KEY = 'fleetnexis_tenant_scope';
export const LOCALE_CACHE_KEY = 'fleetnexis.localeConfig';

const readJson = (storage, key) => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export const getStoredUser = () => readJson(window.localStorage, 'user');

// The API always sends user_role at login; anything else is treated with the
// least privilege (the Router sends such stale sessions back to login).
export const getUserRole = (user = getStoredUser()) => {
  if (!user) return null;
  return Object.values(ROLES).includes(user.user_role) ? user.user_role : ROLES.STAFF;
};

export const isSuperAdmin = (user = getStoredUser()) => getUserRole(user) === ROLES.SUPER_ADMIN;

export const isAdminUser = (user = getStoredUser()) =>
  [ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN].includes(getUserRole(user));

// Staff: view-only in Store and HR, no Finance or Management, own bookings only.
export const isStaff = (user = getStoredUser()) => getUserRole(user) === ROLES.STAFF;

// The tenant a super admin is acting for: { tenant_id, tenant_name, timezone } or null (= all).
export const getTenantScope = () => (isSuperAdmin() ? readJson(window.localStorage, TENANT_SCOPE_KEY) : null);

// Switching tenants changes every list and the display time zone, so start the
// app afresh rather than patching state across pages.
export const setTenantScope = (scope) => {
  if (scope && scope.tenant_id) {
    window.localStorage.setItem(TENANT_SCOPE_KEY, JSON.stringify(scope));
  } else {
    window.localStorage.removeItem(TENANT_SCOPE_KEY);
  }
  window.sessionStorage.removeItem(LOCALE_CACHE_KEY);
  window.location.reload();
};

// Tenant whose data the session sees; null = all tenants (super admin only).
export const getActiveTenantId = (user = getStoredUser()) => {
  if (!user) return null;
  if (isSuperAdmin(user)) {
    const scope = getTenantScope();
    return scope ? Number(scope.tenant_id) : null;
  }
  return user.tenant_id ? Number(user.tenant_id) : null;
};

export const getActiveTenantName = (user = getStoredUser()) => {
  if (!user) return '';
  if (isSuperAdmin(user)) {
    const scope = getTenantScope();
    return scope ? scope.tenant_name : 'All tenants';
  }
  return user.tenant_name || '';
};

// Public website enquiries and the legacy mobile database belong to the
// platform (default) tenant.
export const isPlatformScope = (user = getStoredUser()) => {
  const tenantId = getActiveTenantId(user);
  return tenantId === null || tenantId === DEFAULT_TENANT_ID;
};

// Time zone known before /config/locale answers, so the first render already
// shows the tenant's clock.
export const getStoredTimezone = (user = getStoredUser()) => {
  if (!user) return null;
  if (isSuperAdmin(user)) {
    const scope = getTenantScope();
    return (scope && scope.timezone) || null;
  }
  return user.tenant_timezone || null;
};

/*
 * Make every moment() in the app render in the tenant's zone. The API returns
 * instants (UTC, "...Z"); without this they are shown in the browser's zone,
 * which differs from the tenant's whenever the operator sits elsewhere.
 */
export const applyDefaultTimezone = (zone) => {
  if (zone && moment.tz.zone(zone)) {
    moment.tz.setDefault(zone);
  } else {
    moment.tz.setDefault();
  }
};

// Sidebar entries may declare `roles: [...]` and/or `platformOnly: true`.
// With "All tenants" a super admin's menu is only the top-level entries marked
// `superAdmin: true` (Dashboard and Organisation). After opening a tenant it is
// that tenant's full admin menu.
export const filterNavItems = (items, user = getStoredUser(), nested = false) => {
  const role = getUserRole(user);
  const platform = isPlatformScope(user);
  const platformMenu = role === ROLES.SUPER_ADMIN && !getActiveTenantId(user);
  return (items || [])
    .filter((item) => nested || !platformMenu || item.superAdmin)
    .filter((item) => !item.roles || item.roles.includes(role))
    .filter((item) => !item.platformOnly || platform)
    .map((item) => (item.children ? { ...item, children: filterNavItems(item.children, user, true) } : item))
    .filter((item) => !item.children || item.children.length > 0);
};

applyDefaultTimezone(getStoredTimezone());
